import { OpportunityReport } from "@entities";
import { Box, Button, MenuItem, Select } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { type HyperTableFilter } from "@shared/frontend/hyper-table";
import { ISSUE_TYPE, ISSUE_TYPE_HEB } from "@shared/helpers/constants";
import dayjs from "dayjs";

const toValidDate = (value: unknown): Date | null => {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return Number.isFinite(date.getTime()) ? date : null;
};

const filters: HyperTableFilter<OpportunityReport>[] = [
  { fieldName: "isGoalAchieved", id: "isGoalAchieved" },
  { fieldName: "text", id: "text" },
  { fieldName: "association.user.name", id: "volunteerName", caption: "מתנדב" },
  {
    id: "issueType",
    icon: "literal",
    caption: "סוג בעיה",
    renderFilter: ({ onSave, meta }) => {
      const value = (meta.value as string) ?? "";
      return (
        <Box sx={{ p: 1, minWidth: 220 }}>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={value}
            onChange={(event) => {
              const next = event.target.value as (typeof ISSUE_TYPE)[number] | "";
              onSave({
                value: next
                  ? OpportunityReport.hyperFilter({ issueType: next })
                  : {},
                meta: {
                  value: next,
                  text: next ? ISSUE_TYPE_HEB[next] : "",
                },
              });
            }}
          >
            <MenuItem value="">הכל</MenuItem>
            {ISSUE_TYPE.map((issue) => (
              <MenuItem key={issue} value={issue}>
                {ISSUE_TYPE_HEB[issue]}
              </MenuItem>
            ))}
          </Select>
        </Box>
      );
    },
  },
  {
    id: "createdAt",
    icon: "date",
    caption: "תאריך הדיווח",
    renderFilter: ({ onSave, meta }) => {
      const startDate = toValidDate(meta.startDate);
      const endDate = toValidDate(meta.endDate);
      const buildFilter = (start: Date | null, end: Date | null) => {
        if (!start && !end) return {};
        return OpportunityReport.hyperFilter({
          createdAt: {
            ...(start ? { $gte: dayjs(start).startOf("day").toDate() } : {}),
            ...(end ? { $lte: dayjs(end).endOf("day").toDate() } : {}),
          },
        });
      };
      return (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
          adapterLocale="he"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1 }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <DatePicker
                label="מתאריך"
                value={startDate ? dayjs(startDate) : null}
                format="DD/MM/YYYY"
                onChange={(date) => {
                  const next = date ? dayjs(date).toDate() : null;
                  onSave({
                    value: buildFilter(next, endDate),
                    meta: { startDate: next, endDate },
                  });
                }}
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label="עד תאריך"
                value={endDate ? dayjs(endDate) : null}
                format="DD/MM/YYYY"
                onChange={(date) => {
                  const next = date ? dayjs(date).toDate() : null;
                  onSave({
                    value: buildFilter(startDate, next),
                    meta: { startDate, endDate: next },
                  });
                }}
                slotProps={{ textField: { size: "small" } }}
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              sx={{ alignSelf: "flex-start" }}
              onClick={() =>
                onSave({ value: {}, meta: { startDate: null, endDate: null } })
              }
            >
              נקה סינון
            </Button>
          </Box>
        </LocalizationProvider>
      );
    },
  },
];

export default filters;
